import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
	Avatar,
	IconButton,
	CircularProgress,
	Button,
	Tooltip,
} from "@material-ui/core";
import {
	ArrowBack as ArrowBackIcon,
	Call as CallIcon,
	Videocam as VideocamIcon,
	MoreVert as MoreVertIcon,
	Check as CheckIcon,
	DoneAll as DoneAllIcon,
	Description as DescriptionIcon,
	GetApp as GetAppIcon,
	FileCopy as FileCopyIcon,
	Refresh as RefreshIcon,
	Lock as LockIcon,
	Wifi as WifiIcon,
	SignalCellularAlt as SignalIcon,
	BatteryFull as BatteryIcon,
	Mic as MicIcon,
	InsertEmoticon as EmojiIcon,
	AttachFile as AttachFileIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import api from "../../services/api";
import AudioComponent from "../../components/Audio";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "100vh",
		backgroundColor: "#0f172a",
		backgroundImage:
			"radial-gradient(at 0% 0%, rgba(56, 189, 248, 0.12) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(16, 185, 129, 0.12) 0px, transparent 50%)",
		fontFamily:
			"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
		padding: theme.spacing(2),
		boxSizing: "border-box",
		[theme.breakpoints.down("xs")]: {
			padding: 0,
			backgroundColor: "#efeae2",
		},
	},
	topControlBar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		maxWidth: 440,
		marginBottom: theme.spacing(1.5),
		color: "#e2e8f0",
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	brandInfo: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: "0.85rem",
		fontWeight: 600,
		letterSpacing: "0.3px",
	},
	actionsGroup: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	actionButton: {
		textTransform: "none",
		fontWeight: 600,
		fontSize: "0.78rem",
		borderRadius: 20,
		padding: "4px 12px",
		backgroundColor: "rgba(255, 255, 255, 0.1)",
		color: "#f8fafc",
		border: "1px solid rgba(255, 255, 255, 0.15)",
		"&:hover": {
			backgroundColor: "rgba(255, 255, 255, 0.2)",
		},
	},
	phoneFrame: {
		width: "100%",
		maxWidth: 420,
		height: "840px",
		maxHeight: "92vh",
		backgroundColor: "#000",
		borderRadius: 48,
		border: "11px solid #1e293b",
		boxShadow:
			"0 25px 60px -15px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.1)",
		display: "flex",
		flexDirection: "column",
		position: "relative",
		overflow: "hidden",
		[theme.breakpoints.down("xs")]: {
			maxWidth: "100%",
			height: "100vh",
			maxHeight: "100vh",
			borderRadius: 0,
			border: "none",
			boxShadow: "none",
		},
	},
	dynamicIslandWrapper: {
		position: "absolute",
		top: 10,
		left: "50%",
		transform: "translateX(-50%)",
		zIndex: 30,
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	dynamicIsland: {
		width: 105,
		height: 24,
		backgroundColor: "#000",
		borderRadius: 18,
		boxShadow: "0 0 3px rgba(255, 255, 255, 0.15)",
	},
	statusBar: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		height: 38,
		padding: "0 22px",
		backgroundColor: "#005c4b",
		color: "#ffffff",
		fontSize: "0.78rem",
		fontWeight: 600,
		zIndex: 20,
		flexShrink: 0,
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	statusIcons: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		"& svg": {
			fontSize: "1rem",
		},
	},
	chatHeader: {
		display: "flex",
		alignItems: "center",
		padding: "8px 10px",
		backgroundColor: "#005c4b",
		color: "#ffffff",
		zIndex: 20,
		boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
		flexShrink: 0,
	},
	backBtn: {
		color: "#ffffff",
		padding: 4,
		marginRight: 2,
	},
	headerAvatar: {
		width: 38,
		height: 38,
		marginRight: 10,
		backgroundColor: "#128c7e",
		fontSize: "1rem",
		fontWeight: 600,
	},
	headerInfo: {
		flex: 1,
		minWidth: 0,
	},
	contactName: {
		fontSize: "0.98rem",
		fontWeight: 600,
		lineHeight: 1.2,
		color: "#ffffff",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	contactSubtitle: {
		fontSize: "0.74rem",
		color: "#d1fae5",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		marginTop: 2,
	},
	headerIcons: {
		display: "flex",
		alignItems: "center",
		gap: 0,
		"& button": {
			color: "#ffffff",
			padding: 6,
		},
	},
	chatBody: {
		flex: 1,
		backgroundColor: "#efeae2",
		backgroundImage:
			"radial-gradient(#d1d5db 0.75px, transparent 0.75px), radial-gradient(#d1d5db 0.75px, #efeae2 0.75px)",
		backgroundSize: "24px 24px",
		backgroundPosition: "0 0, 12px 12px",
		overflowY: "auto",
		padding: "12px 12px 16px 12px",
		display: "flex",
		flexDirection: "column",
		gap: 6,
		"&::-webkit-scrollbar": {
			width: 5,
		},
		"&::-webkit-scrollbar-thumb": {
			backgroundColor: "rgba(0, 0, 0, 0.2)",
			borderRadius: 3,
		},
	},
	encryptionNotice: {
		backgroundColor: "#ffeecd",
		color: "#54656f",
		fontSize: "0.72rem",
		padding: "6px 12px",
		borderRadius: 8,
		textAlign: "center",
		margin: "4px auto 10px auto",
		maxWidth: "88%",
		boxShadow: "0 1px 1px rgba(0, 0, 0, 0.08)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 6,
		lineHeight: 1.3,
	},
	dateDivider: {
		alignSelf: "center",
		backgroundColor: "#ffffff",
		color: "#54656f",
		fontSize: "0.7rem",
		fontWeight: 600,
		padding: "4px 10px",
		borderRadius: 7,
		boxShadow: "0 1px 2px rgba(0, 0, 0, 0.12)",
		margin: "6px 0",
		textTransform: "uppercase",
	},
	messageRow: {
		display: "flex",
		width: "100%",
	},
	messageRowIn: {
		justifyContent: "flex-start",
	},
	messageRowOut: {
		justifyContent: "flex-end",
	},
	bubble: {
		maxWidth: "82%",
		padding: "6px 9px 5px 9px",
		borderRadius: 8,
		boxShadow: "0 1px 1.5px rgba(0, 0, 0, 0.15)",
		position: "relative",
		wordBreak: "break-word",
		fontSize: "0.88rem",
		lineHeight: 1.35,
	},
	bubbleIn: {
		backgroundColor: "#ffffff",
		color: "#111b21",
		borderTopLeftRadius: 0,
	},
	bubbleOut: {
		backgroundColor: "#d9fdd3",
		color: "#111b21",
		borderTopRightRadius: 0,
	},
	senderBadge: {
		fontSize: "0.68rem",
		fontWeight: 700,
		marginBottom: 3,
		color: "#128c7e",
	},
	quotedBox: {
		backgroundColor: "rgba(0, 0, 0, 0.05)",
		borderLeft: "4px solid #00a884",
		padding: "4px 8px",
		borderRadius: 5,
		marginBottom: 6,
		fontSize: "0.75rem",
	},
	quotedAuthor: {
		fontWeight: 600,
		color: "#00a884",
		marginBottom: 2,
	},
	quotedBody: {
		color: "#54656f",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	mediaImage: {
		width: "100%",
		maxWidth: 260,
		maxHeight: 280,
		objectFit: "cover",
		borderRadius: 6,
		cursor: "pointer",
		marginBottom: 4,
	},
	mediaVideo: {
		width: "100%",
		maxWidth: 260,
		borderRadius: 6,
		marginBottom: 4,
	},
	docCard: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		backgroundColor: "rgba(0, 0, 0, 0.04)",
		padding: "8px 10px",
		borderRadius: 6,
		textDecoration: "none",
		color: "inherit",
		marginBottom: 4,
	},
	docIcon: {
		color: "#dc2626",
	},
	docName: {
		fontSize: "0.78rem",
		fontWeight: 600,
		maxWidth: 180,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	messageMeta: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 3,
		fontSize: "0.68rem",
		color: "#667781",
		marginTop: 2,
		marginLeft: 16,
		float: "right",
	},
	checkIcon: {
		fontSize: "0.95rem",
		color: "#53bdeb",
	},
	checkPending: {
		fontSize: "0.95rem",
		color: "#8696a0",
	},
	chatFooter: {
		display: "flex",
		alignItems: "center",
		padding: "8px 10px",
		backgroundColor: "#f0f2f5",
		borderTop: "1px solid #e2e8f0",
		gap: 6,
		flexShrink: 0,
	},
	fakeInput: {
		flex: 1,
		backgroundColor: "#ffffff",
		borderRadius: 20,
		padding: "8px 14px",
		fontSize: "0.82rem",
		color: "#94a3b8",
		userSelect: "none",
	},
	footerIcon: {
		color: "#54656f",
		padding: 4,
	},
	homeBarWrapper: {
		height: 18,
		backgroundColor: "#f0f2f5",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	homeBar: {
		width: 120,
		height: 4,
		backgroundColor: "#cbd5e1",
		borderRadius: 2,
	},
	loadingContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		color: "#ffffff",
		gap: 16,
	},
	errorCard: {
		backgroundColor: "#1e293b",
		color: "#f8fafc",
		padding: theme.spacing(4),
		borderRadius: 16,
		textAlign: "center",
		maxWidth: 400,
		boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.5)",
	},
}));

const formatMessageBody = (text) => {
	if (!text) return null;

	// Renderiza quebras de linha preservadas
	const lines = text.split("\n");
	return lines.map((line, idx) => (
		<React.Fragment key={idx}>
			{line}
			{idx < lines.length - 1 && <br />}
		</React.Fragment>
	));
};

const TicketPreview = () => {
	const classes = useStyles();
	const { token } = useParams();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const chatBodyRef = useRef(null);

	const loadTicketData = async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await api.get(`/public/tickets/${token}`);
			setData(res.data);
			setLoading(false);
			setTimeout(() => {
				if (chatBodyRef.current) {
					chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
				}
			}, 100);
		} catch (err) {
			console.error("Erro ao carregar ticket público:", err);
			setError(
				"Atendimento não encontrado ou o link de visualização é inválido."
			);
			setLoading(false);
		}
	};

	useEffect(() => {
		if (token) {
			loadTicketData();
		}
	}, [token]);

	const handleCopyLink = () => {
		try {
			navigator.clipboard.writeText(window.location.href);
			toast.success("Link público copiado com sucesso! 🚀");
		} catch (e) {
			toast.info("Copie o link da barra de navegação.");
		}
	};

	if (loading) {
		return (
			<div className={classes.root}>
				<div className={classes.loadingContainer}>
					<CircularProgress style={{ color: "#10b981" }} size={48} />
					<span style={{ fontSize: "0.95rem", fontWeight: 500 }}>
						Carregando conversa...
					</span>
				</div>
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className={classes.root}>
				<div className={classes.errorCard}>
					<LockIcon style={{ fontSize: 48, color: "#f59e0b", marginBottom: 12 }} />
					<h3 style={{ margin: "0 0 8px 0" }}>Acesso Indisponível</h3>
					<p style={{ fontSize: "0.88rem", color: "#94a3b8", lineHeight: 1.5 }}>
						{error || "Não foi possível carregar o atendimento compartilhado."}
					</p>
					<Button
						variant="outlined"
						style={{ marginTop: 16, color: "#f8fafc", borderColor: "#475569" }}
						onClick={() => window.location.reload()}
					>
						Tentar Novamente
					</Button>
				</div>
			</div>
		);
	}

	const { ticket, contact, whatsapp, messages } = data;
	const contactTitle = contact?.name || contact?.number || "Contato";
	const whatsappTitle = whatsapp?.name ? `WhatsApp • ${whatsapp.name}` : "WhatsApp";

	return (
		<div className={classes.root}>
			{/* Barra superior de controle (desktop) */}
			<div className={classes.topControlBar}>
				<div className={classes.brandInfo}>
					<LockIcon style={{ fontSize: "1rem", color: "#10b981" }} />
					<span>Visualização de Atendimento #{ticket.id}</span>
				</div>
				<div className={classes.actionsGroup}>
					<Button
						className={classes.actionButton}
						startIcon={<FileCopyIcon style={{ fontSize: "0.95rem" }} />}
						onClick={handleCopyLink}
					>
						Copiar Link
					</Button>
					<Tooltip title="Atualizar mensagens">
						<IconButton
							size="small"
							style={{ color: "#e2e8f0", padding: 6 }}
							onClick={loadTicketData}
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</div>
			</div>

			{/* Moldura do Celular */}
			<div className={classes.phoneFrame}>
				{/* Ilha dinâmica do iPhone */}
				<div className={classes.dynamicIslandWrapper}>
					<div className={classes.dynamicIsland} />
				</div>

				{/* Status bar */}
				<div className={classes.statusBar}>
					<span>9:41</span>
					<div className={classes.statusIcons}>
						<SignalIcon />
						<WifiIcon />
						<BatteryIcon />
					</div>
				</div>

				{/* WhatsApp Header */}
				<div className={classes.chatHeader}>
					<IconButton className={classes.backBtn} size="small" disabled>
						<ArrowBackIcon fontSize="small" />
					</IconButton>
					<Avatar
						src={contact?.profilePicUrl}
						className={classes.headerAvatar}
					>
						{contactTitle.charAt(0).toUpperCase()}
					</Avatar>
					<div className={classes.headerInfo}>
						<div className={classes.contactName}>{contactTitle}</div>
						<div className={classes.contactSubtitle}>{whatsappTitle}</div>
					</div>
					<div className={classes.headerIcons}>
						<IconButton size="small" disabled>
							<VideocamIcon fontSize="small" />
						</IconButton>
						<IconButton size="small" disabled>
							<CallIcon fontSize="small" />
						</IconButton>
						<IconButton size="small" disabled>
							<MoreVertIcon fontSize="small" />
						</IconButton>
					</div>
				</div>

				{/* Chat Body */}
				<div className={classes.chatBody} ref={chatBodyRef}>
					<div className={classes.encryptionNotice}>
						<LockIcon style={{ fontSize: "0.85rem", flexShrink: 0 }} />
						<span>
							Atendimento compartilhado em modo somente-leitura.
						</span>
					</div>

					{ticket?.createdAt && (
						<div className={classes.dateDivider}>
							Início: {format(parseISO(ticket.createdAt), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
						</div>
					)}

					{messages &&
						messages.map((msg) => {
							const isFromMe = msg.fromMe;
							const formattedTime = msg.createdAt
								? format(parseISO(msg.createdAt), "HH:mm")
								: "";

							return (
								<div
									key={msg.id}
									className={`${classes.messageRow} ${
										isFromMe ? classes.messageRowOut : classes.messageRowIn
									}`}
								>
									<div
										className={`${classes.bubble} ${
											isFromMe ? classes.bubbleOut : classes.bubbleIn
										}`}
									>
										{/* Badge de remetente caso queira destacar IA */}
										{isFromMe && msg.sentBy === "ai" && (
											<div className={classes.senderBadge}>🤖 Assistente Virtual</div>
										)}

										{/* Mensagem Respondida (Quoted) */}
										{msg.quotedMsg && (
											<div className={classes.quotedBox}>
												<div className={classes.quotedAuthor}>
													{msg.quotedMsg.fromMe ? "Você" : msg.quotedMsg.contact?.name || "Contato"}
												</div>
												<div className={classes.quotedBody}>
													{msg.quotedMsg.body || "Mídia"}
												</div>
											</div>
										)}

										{/* Mídia: Imagem */}
										{msg.mediaType === "image" && msg.mediaUrl && (
											<a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer">
												<img
													src={msg.mediaUrl}
													alt="Imagem do atendimento"
													className={classes.mediaImage}
													loading="lazy"
												/>
											</a>
										)}

										{/* Mídia: Vídeo */}
										{msg.mediaType === "video" && msg.mediaUrl && (
											<video
												src={msg.mediaUrl}
												controls
												className={classes.mediaVideo}
											/>
										)}

										{/* Mídia: Áudio */}
										{msg.mediaType === "audio" && msg.mediaUrl && (
											<AudioComponent url={msg.mediaUrl} />
										)}

										{/* Mídia: Documento */}
										{msg.mediaType === "application" && msg.mediaUrl && (
											<a
												href={msg.mediaUrl}
												target="_blank"
												rel="noopener noreferrer"
												className={classes.docCard}
												download
											>
												<DescriptionIcon className={classes.docIcon} />
												<span className={classes.docName}>
													{msg.body || "Documento / PDF"}
												</span>
												<GetAppIcon fontSize="small" style={{ marginLeft: "auto", color: "#64748b" }} />
											</a>
										)}

										{/* Texto da mensagem */}
										{msg.body && msg.mediaType !== "application" && (
											<div>{formatMessageBody(msg.body)}</div>
										)}

										{/* Metadados: Hora e Checks */}
										<div className={classes.messageMeta}>
											<span>{formattedTime}</span>
											{isFromMe && (
												msg.ack >= 2 ? (
													<DoneAllIcon className={classes.checkIcon} />
												) : (
													<CheckIcon className={classes.checkPending} />
												)
											)}
										</div>
									</div>
								</div>
							);
						})}
				</div>

				{/* Fake Footer / Modo Somente Leitura */}
				<div className={classes.chatFooter}>
					<IconButton size="small" disabled className={classes.footerIcon}>
						<EmojiIcon fontSize="small" />
					</IconButton>
					<IconButton size="small" disabled className={classes.footerIcon}>
						<AttachFileIcon fontSize="small" />
					</IconButton>
					<div className={classes.fakeInput}>Visualização somente leitura</div>
					<IconButton size="small" disabled className={classes.footerIcon}>
						<MicIcon fontSize="small" />
					</IconButton>
				</div>

				{/* Barra inferior de navegação (iPhone Home Indicator) */}
				<div className={classes.homeBarWrapper}>
					<div className={classes.homeBar} />
				</div>
			</div>
		</div>
	);
};

export default TicketPreview;
